/* @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';

import logger from 'js/app/loggerSingleton';
import requestCountry from 'js/lib/requestCountry';

import type { CSSProperties } from '@coursera/coursera-ui';

import NonRecurringChoiceScreenReaderDescription from 'bundles/enroll-modal/components/non-recurring/NonRecurringChoiceScreenReaderDescription';
import ReactPriceDisplay from 'bundles/ownable-products-common/components/ReactPriceDisplay';
import type { ProductPrices } from 'bundles/ownable-products-common/hooks/useGetProductPrices';

import _t from 'i18n!nls/s12n-enroll';

export type Props = {
  choice: string;
  currentChoice: string;
  productPrice: ProductPrices | undefined;
  numOfMonths: number;
  numOfHours: number;
  percentageOff: number;
  onClick: () => void;
  title: JSX.Element | string;
  duration?: JSX.Element | string;
  showRecommendedPaceBadge?: boolean;
  isFullDiscount?: boolean;
};

const useStyles = (): Record<string, CSSProperties> => {
  return {
    nonRecurringChoice: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: 0,
      padding: `var(--cds-spacing-400) 0 var(--cds-spacing-400) var(--cds-spacing-300)`,
      cursor: 'pointer',
      position: 'relative',
    },
    radioInput: {
      opacity: 0,
      ':checked~span.cif-stack': {
        '.cif-circle': {
          color: 'var(--cds-color-blue-600)',
        },
      },
      ':focus-visible~span.cif-stack': {
        outline: '2px solid var(--cds-color-blue-600)',
        borderRadius: '50%',
      },
    },
    choiceButton: {
      position: 'absolute !important',
      left: '-10px',
    },
    circleThin: {
      color: 'var(--cds-color-darken-100)',
    },
    circle: {
      color: 'var(--cds-color-neutral-primary-invert)',
    },
    choiceContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
    },
    choiceTitle: {
      fontSize: '16px',
      lineHeight: '30px',
      fontFamily: 'OpenSans',
      marginBottom: 0,
    },
    details: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'baseline',
    },
    priceDetails: {
      alignItems: 'flex-end',
    },
    price: {
      fontSize: '16px',
      lineHeight: '30px',
      fontFamily: 'OpenSans',
      fontWeight: 700,
      marginBottom: 0,
    },
    priceExtras: {
      fontWeight: 400,
      marginBottom: '6px',
    },
    badge: {
      width: 'auto',
      display: 'inline-flex',
      borderRadius: '4px',
      textTransform: 'uppercase',
      padding: '0 4px',
      marginBottom: 0,
      fontWeight: 700,
      fontSize: '12px',
      lineHeight: '18px',
    },
    transparentBadge: {
      background: 'transparent !important',
      padding: 0,
    },
    durationBadge: {
      background: 'var(--cds-color-neutral-background-primary-weak)',
      color: 'var(--cds-color-grey-400)',
    },
    discountBadge: {
      background: 'var(--cds-color-yellow-200)',
      color: 'var(--cds-color-neutral-primary)',
    },
    recommendedPaceBadge: {
      background: 'var(--cds-color-green-100)',
      marginTop: '8px',
    },
  };
};

const FormattedPrice = ({
  totalPrice,
  currencyCode,
  numOfMonths,
  percentageOff,
}: {
  totalPrice: number;
  currencyCode: string;
  numOfMonths: number;
  percentageOff: number;
}) => {
  const styles = useStyles();

  if (numOfMonths && requestCountry.get() === 'IN') {
    return (
      <>
        <div>
          <span css={styles.price}>
            <ReactPriceDisplay value={Math.round(totalPrice / numOfMonths)} currency={currencyCode} />
          </span>
          <span css={styles.priceExtras}>/{_t('month')}</span>
        </div>

        <div css={styles.priceExtras}>
          <span>{_t('Total')}&nbsp;</span>
          <ReactPriceDisplay value={totalPrice} currency={currencyCode} />
        </div>
      </>
    );
  }

  return (
    <>
      <div css={styles.price}>
        <ReactPriceDisplay value={totalPrice} currency={currencyCode} />
      </div>

      {percentageOff > 0 && (
        <div css={[styles.badge, styles.discountBadge]}>{_t('#{percentageOff}% off', { percentageOff })}</div>
      )}
    </>
  );
};

const NonRecurringChoice = ({
  choice,
  currentChoice,
  productPrice,
  numOfMonths,
  numOfHours,
  percentageOff,
  onClick,
  title,
  duration,
  showRecommendedPaceBadge = false,
  isFullDiscount = false,
}: Props) => {
  const styles = useStyles();

  const descriptionId = `non-recurring-choice-description-${choice}`;
  const inputId = `non-recurring-choice-${choice}`;

  if (!productPrice) {
    logger.error('Missing price for payment pass option');
    return null;
  }

  return (
    <label data-testid="rc-NonRecurringChoice" htmlFor={inputId} css={styles.nonRecurringChoice}>
      <input
        type="radio"
        id={inputId}
        value={choice}
        checked={choice === currentChoice}
        onClick={onClick}
        aria-describedby={descriptionId}
        css={styles.radioInput}
      />

      <NonRecurringChoiceScreenReaderDescription
        numOfMonths={numOfMonths}
        numOfHours={numOfHours}
        productPrice={productPrice}
        percentageOff={percentageOff}
        isRecommended={showRecommendedPaceBadge}
      />

      <span aria-hidden="true" className="cif-stack" css={styles.choiceButton}>
        <i className="cif-circle-thin cif-stack-2x" css={styles.circleThin} />
        <i className="cif-circle cif-stack-1x" css={styles.circle} />
      </span>

      <div aria-hidden="true" css={styles.choiceContainer}>
        <div className="context-details" css={[styles.details, styles.contextDetails]}>
          <h4 css={styles.choiceTitle} id={descriptionId}>
            {title}
          </h4>

          <div css={[styles.badge, styles.durationBadge, showRecommendedPaceBadge && styles.transparentBadge]}>
            {duration}
          </div>

          {showRecommendedPaceBadge && (
            <div css={[styles.badge, styles.recommendedPaceBadge]}>{_t('Recommended Pace')}</div>
          )}
        </div>

        <div className="price-details" css={[styles.details, styles.priceDetails]}>
          {isFullDiscount ? (
            _t('Free')
          ) : (
            <FormattedPrice
              totalPrice={productPrice.amount}
              currencyCode={productPrice.currencyCode}
              numOfMonths={numOfMonths}
              percentageOff={percentageOff}
            />
          )}
        </div>
      </div>
    </label>
  );
};

export default NonRecurringChoice;
